import { inject, Injectable } from '@angular/core';
import type { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { filterBoolean, LocalStorageService } from '@core/common-legacy';
import { DashboardRecentsActions, DashboardRecentsSelectors } from '@core/dashboard-data';
import type {
  RecentNavigationSnapshot,
  ResourceKind,
  SnapshotResolver,
  VisitedEntityBase,
} from '@core/dashboard-types';
import { uniqByKey } from '@core/utils-legacy';
import { WalkthroughStepsService } from '@core/walkthrough-legacy';

/**
 * Refactor this after old dashboard removal
 */
@Injectable({ providedIn: 'root' })
export class VisitedEntitiesHistoryService<T extends VisitedEntityBase<ResourceKind, {}>> {
  public static readonly lsKey = 'visited-entities-history';
  private static readonly limit = 10;

  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  public visitedEntities$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>(
    this.localStorageService.get(VisitedEntitiesHistoryService.lsKey, []),
  );
  public recentsMeta = this.store.selectSignal(DashboardRecentsSelectors.meta);
  public recentsData = this.store.selectSignal(DashboardRecentsSelectors.data);

  constructor(
    private localStorageService: LocalStorageService,
    private walkthroughStepsService: WalkthroughStepsService,
  ) {
    this.visitedEntities$.subscribe(entities =>
      this.localStorageService.set(VisitedEntitiesHistoryService.lsKey, entities),
    );
  }

  public updateHistory(entity: T): void {
    this.store.dispatch(
      DashboardRecentsActions.update({ payload: { type: entity.kind, id: entity.id } }),
    );
    if (!this.walkthroughStepsService.activeStep()) {
      const entities = uniqByKey([entity, ...this.visitedEntities$.getValue()], 'id').splice(
        0,
        VisitedEntitiesHistoryService.limit,
      );
      this.visitedEntities$.next(entities);
    }
  }

  public loadRecents() {
    this.store.dispatch(DashboardRecentsActions.fetch());
  }

  public reloadRecentsOnUpdate() {
    return this.actions$.pipe(
      ofType(DashboardRecentsActions.updateSuccess),
      tap(() => this.store.dispatch(DashboardRecentsActions.fetch())),
    );
  }

  public getRecents() {
    return this.store.select(DashboardRecentsSelectors.data).pipe(filterBoolean);
  }
}

export function saveNavigationHistory(resolver: SnapshotResolver<RecentNavigationSnapshot>) {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const resultResolver = resolver(route, state);
    const historyService = inject(VisitedEntitiesHistoryService);

    resultResolver.subscribe(result => {
      historyService.updateHistory(result);

      return result;
    });
  };
}
